import React, { createContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import detectEthereumProvider from '@metamask/detect-provider';
import { EthereumProvider } from '@walletconnect/ethereum-provider';
import { BrowserProvider, JsonRpcProvider, Contract } from 'ethers';
import { useToast } from '@/hooks/useToast';
import i18n from '@/i18n';
import { isMobile, openInMetaMaskApp } from '@/utils/platform';
import { useDevLog } from '@/contexts/DevLogContext';
import { useAuth } from '@/contexts/AuthContext';
import { useAdmin } from '@/contexts/AdminContext';
import { STABLECOIN_METADATA_BY_CHAIN, ERC20_BALANCEOF_SELECTOR } from '@/config/stablecoinContracts';
import { getRpcUrls } from '@/config/rpcEndpoints';
import { normalizeChainId } from '@/utils/networkSync';

import baseIcon from '@/assets/images/base.svg';
import arbitrumIcon from '@/assets/images/arbitrum.svg';
import polygonIcon from '@/assets/images/polygon.svg';
import ethereumIcon from '@/assets/images/ethereum.svg';

export const WalletConnectionContext = createContext();

/** Redes suportadas pela aplicação (MetaMask / WalletConnect).
 *  isTestnet: true para redes de teste; false para mainnets.
 */
export const SUPPORTED_NETWORKS = [
  { chainId: '0xaa36a7', name: 'Sepolia', icon: ethereumIcon, isTestnet: true }, // Ethereum Sepolia (Testnet) — chainId 11155111
  { chainId: '0x14a34', name: 'Base Sepolia', icon: baseIcon, isTestnet: true }, // chainId 84532
  { chainId: '0x2105', name: 'Base', icon: baseIcon, isTestnet: false },
  { chainId: '0xa4b1', name: 'Arbitrum', icon: arbitrumIcon, isTestnet: false },
  { chainId: '0x89', name: 'Polygon', icon: polygonIcon, isTestnet: false },
];

/** Redes exibidas quando a carteira Phantom (EVM) está conectada. Sem Arbitrum (não aparece na UI da Phantom). */
export const PHANTOM_EVM_NETWORKS = [
  { chainId: '0x2105', name: 'Base', icon: baseIcon, isTestnet: false },
  { chainId: '0x89', name: 'Polygon', icon: polygonIcon, isTestnet: false },
  { chainId: '0x14a34', name: 'Base Sepolia', icon: baseIcon, isTestnet: true },
  { chainId: '0xaa36a7', name: 'Sepolia', icon: ethereumIcon, isTestnet: true }, // Ethereum Sepolia (Testnet)
];

/** ABI mínima para balanceOf (ERC-20). */
const ERC20_ABI = ['function balanceOf(address owner) view returns (uint256)'];

/** WalletConnect Project ID (multi-wallet / mobile) */
const WALLETCONNECT_PROJECT_ID = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || '';

/** Chave localStorage para última carteira conectada (metamask | phantom | walletconnect) */
const LAST_WALLET_STORAGE_KEY = 'wallet_lastConnectedWallet';

/** Provider Phantom Solana (window.phantom.solana ou window.solana com isPhantom). */
function getPhantomSolanaFromWindow() {
  if (typeof window === 'undefined') return null;
  const phantomSolana = window.phantom?.solana;
  if (phantomSolana && phantomSolana.isPhantom) return phantomSolana;
  const legacy = window.solana;
  if (legacy && legacy.isPhantom) return legacy;
  return null;
}

/** Provider Phantom EVM (Base, Base Sepolia, etc.) — EIP-1193. */
function getPhantomEthereumFromWindow() {
  if (typeof window === 'undefined') return null;
  const eth = window.phantom?.ethereum;
  if (eth && eth.isPhantom) return eth;
  if (window.ethereum?.isPhantom) return window.ethereum;
  return null;
}

async function fetchBalanceViaPublicRpc(chainId, address) {
  const urls = getRpcUrls(chainId, true);
  for (const rpcUrl of urls) {
    try {
      const provider = new JsonRpcProvider(rpcUrl);
      const balanceWei = await provider.getBalance(address);
      return balanceWei;
    } catch {
      // RPC indisponível, tenta próximo
    }
  }
  return null;
}

async function fetchEthBalance(provider, address) {
  const weiToEth = (wei) => (Number(wei) / 1e18).toFixed(6);
  const tryProviderRequest = async () => {
    const raw = await provider.request({
      method: 'eth_getBalance',
      params: [address, 'latest'],
    });
    return BigInt(raw);
  };
  let wei = await tryProviderRequest();
  if (wei > 0n) return weiToEth(wei);
  try {
    const chainId = await provider.request({ method: 'eth_chainId' });
    const weiFromRpc = await fetchBalanceViaPublicRpc(chainId, address);
    if (weiFromRpc !== null && weiFromRpc > 0n) return weiToEth(weiFromRpc);
  } catch {
    // Fallback RPC falhou
  }
  await new Promise((r) => setTimeout(r, 800));
  wei = await tryProviderRequest();
  if (wei > 0n) return weiToEth(wei);
  try {
    const ethersProvider = new BrowserProvider(provider);
    const balanceWei = await ethersProvider.getBalance(address);
    if (balanceWei > 0n) return weiToEth(balanceWei);
  } catch {
    // ethers getBalance falhou
  }
  return weiToEth(wei);
}

const CHAIN_DISPLAY_NAMES = {
  '0x1': 'Ethereum',
  '0x89': 'Polygon',
  '0xa4b1': 'Arbitrum',
  '0x2105': 'Base',
};

export function getChainDisplayName(chainId) {
  const key = normalizeChainId(chainId);
  return CHAIN_DISPLAY_NAMES[key] || (key ? `Rede ${key}` : null);
}

async function fetchStablecoinBalances(chainId, address, metamaskProvider = null) {
  const key = normalizeChainId(chainId);
  const tokens = STABLECOIN_METADATA_BY_CHAIN[key] || STABLECOIN_METADATA_BY_CHAIN['0x1'] || [];
  const rpcs = getRpcUrls(chainId, true);
  const balances = {};
  let totalRaw = 0;
  if (metamaskProvider) {
    try {
      const metamaskChainId = await metamaskProvider.request({ method: 'eth_chainId' });
      const normalizedMetamaskChain = normalizeChainId(metamaskChainId);
      if (normalizedMetamaskChain === key) {
        const fetchTokenBalance = async (tokenAddress, walletAddress) => {
          const paddedAddress = walletAddress.slice(2).toLowerCase().padStart(64, '0');
          const callData = ERC20_BALANCEOF_SELECTOR + paddedAddress;
          const result = await metamaskProvider.request({
            method: 'eth_call',
            params: [{ to: tokenAddress, data: callData }, 'latest'],
          });
          if (!result || result === '0x' || result === '0x0') return 0n;
          return BigInt(result);
        };
        for (const token of tokens) {
          try {
            const raw = await fetchTokenBalance(token.address, address);
            const value = Number(raw) / Math.pow(10, token.decimals);
            if (value > 0) {
              balances[token.symbol] = value.toFixed(2);
              totalRaw += value;
            }
          } catch {
            // Token não disponível
          }
        }
        return { balances, total: totalRaw.toFixed(2) };
      }
    } catch {
      // Erro ao verificar rede do MetaMask
    }
  }
  for (const rpcUrl of rpcs) {
    try {
      const provider = new JsonRpcProvider(rpcUrl, undefined, { staticNetwork: true });
      await provider.getBlockNumber();
      for (const token of tokens) {
        try {
          const paddedAddress = address.slice(2).toLowerCase().padStart(64, '0');
          const callData = ERC20_BALANCEOF_SELECTOR + paddedAddress;
          const result = await provider.call({ to: token.address, data: callData });
          const raw = (!result || result === '0x' || result === '0x0') ? 0n : BigInt(result);
          const value = Number(raw) / Math.pow(10, token.decimals);
          if (value > 0) {
            balances[token.symbol] = value.toFixed(2);
            totalRaw += value;
          }
        } catch {
          // Token não disponível
        }
      }
      return { balances, total: totalRaw.toFixed(2) };
    } catch {
      continue;
    }
  }
  return { balances: {}, total: '0' };
}

async function fetchEthBalanceWithFallbackChains(provider, address) {
  const weiToEth = (wei) => (Number(wei) / 1e18).toFixed(6);
  const chainId = await provider.request({ method: 'eth_chainId' });
  const key = normalizeChainId(chainId);
  let wei = await fetchBalanceViaPublicRpc(chainId, address);
  if (wei !== null && wei > 0n) {
    return { balance: weiToEth(wei), balanceChainId: key };
  }
  if (key === '0x1') {
    wei = await fetchBalanceViaPublicRpc('0x2105', address);
    if (wei !== null && wei > 0n) return { balance: weiToEth(wei), balanceChainId: '0x2105' };
  }
  const balanceStr = await fetchEthBalance(provider, address);
  return { balance: balanceStr, balanceChainId: key };
}

export function WalletConnectionProvider({ children }) {
  const { user } = useAuth();
  const { isCurrentUserAdmin: allowTestnets } = useAdmin();

  const filteredSupportedNetworks = useMemo(() => {
    const mainnets = SUPPORTED_NETWORKS.filter((n) => !n.isTestnet);
    // Regra: admins veem todas as redes (mainnet + testnets); não-admins só mainnets.
    if (allowTestnets) return SUPPORTED_NETWORKS;
    if (mainnets.length > 0) return mainnets;
    return SUPPORTED_NETWORKS;
  }, [allowTestnets]);

  const filteredPhantomEvmNetworks = useMemo(() => {
    const mainnets = PHANTOM_EVM_NETWORKS.filter((n) => !n.isTestnet);
    if (allowTestnets) return PHANTOM_EVM_NETWORKS;
    if (mainnets.length > 0) return mainnets;
    return PHANTOM_EVM_NETWORKS;
  }, [allowTestnets]);

  const [connectedWallet, setConnectedWallet] = useState(null);
  const [walletAddress, setWalletAddress] = useState(null);
  const [walletChain, setWalletChain] = useState(null);
  const [walletBalance, setWalletBalance] = useState('0');
  const [walletBalanceChainId, setWalletBalanceChainId] = useState(null);
  const [walletStablecoinBalances, setWalletStablecoinBalances] = useState({});
  const [walletStablecoinTotal, setWalletStablecoinTotal] = useState('0');
  const [walletStablecoinChainId, setWalletStablecoinChainId] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedChainId = window.localStorage.getItem('wallet_selectedNetworkChainId');
      if (savedChainId) {
        const savedNetwork = SUPPORTED_NETWORKS.find((n) => n.chainId === savedChainId);
        if (savedNetwork) return savedNetwork;
      }
    }
    return SUPPORTED_NETWORKS[0];
  });
  const [isConnecting, setIsConnecting] = useState(false);
  const [isWalletInitializing, setIsWalletInitializing] = useState(true);
  const [isPhantomEVM, setIsPhantomEVM] = useState(false);
  const [error, setError] = useState(null);
  const [provider, setProvider] = useState(null);
  const { success, error: showError } = useToast();
  const providerRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const selectedNetworkRef = useRef(SUPPORTED_NETWORKS[0]);
  const walletConnectProviderRef = useRef(null);
  const lastRequestAccountsAtRef = useRef(0);
  const mmLoopInvokeCountRef = useRef(0);
  const lastNetworkChangeRef = useRef({ source: 'auto', at: 0 });
  const { log: devLog, error: devLogError } = useDevLog();

  const effectiveFilteredNetworks = useMemo(
    () =>
      connectedWallet === 'phantom' && isPhantomEVM ? filteredPhantomEvmNetworks : filteredSupportedNetworks,
    [connectedWallet, isPhantomEVM, filteredPhantomEvmNetworks, filteredSupportedNetworks]
  );

  /** Cooldown (ms) após eth_requestAccounts para evitar loop do prompt "Ir para MetaMask?" */
  const WALLET_CONNECT_COOLDOWN_MS = 30000;

  const mmLog = useCallback((msg, data) => {
    const ts = new Date().toISOString();
    devLog(`[MM_LOOP] ${ts} ${msg}`, data);
  }, [devLog]);
  const mmLogErr = useCallback((msg, err) => {
    const ts = new Date().toISOString();
    devLogError(`[MM_LOOP] ${ts} ${msg}`, err);
  }, [devLogError]);

  useEffect(() => {
    selectedNetworkRef.current = selectedNetwork;
    if (typeof window !== 'undefined' && selectedNetwork?.chainId) {
      window.localStorage.setItem('wallet_selectedNetworkChainId', selectedNetwork.chainId);
    }
  }, [selectedNetwork]);

  // Garante que a rede selecionada respeite as regras de acesso (mainnet vs testnets por role)
  useEffect(() => {
    if (!effectiveFilteredNetworks || effectiveFilteredNetworks.length === 0) return;
    const existsInFiltered = effectiveFilteredNetworks.some(
      (n) => n.chainId === selectedNetwork?.chainId,
    );
    if (!existsInFiltered) {
      const fallback = effectiveFilteredNetworks[0];
      setSelectedNetwork(fallback);
      selectedNetworkRef.current = fallback;
      lastNetworkChangeRef.current = { source: 'auto', at: Date.now() };
      if (typeof window !== 'undefined' && fallback?.chainId) {
        window.localStorage.setItem('wallet_selectedNetworkChainId', fallback.chainId);
      }
    }
  }, [effectiveFilteredNetworks, selectedNetwork]);

  // Sempre que a carteira mudar de rede, sincroniza automaticamente a rede selecionada da aplicação
  // com a rede da carteira, quando essa rede estiver na lista de redes suportadas.
  useEffect(() => {
    if (!walletChain) return;

    const last = lastNetworkChangeRef.current;
    if (last && last.source === 'user' && Date.now() - last.at < 2000) {
      return;
    }

    const normalizedWalletChain = normalizeChainId(walletChain);
    if (!normalizedWalletChain) return;

    const match = effectiveFilteredNetworks.find((n) => normalizeChainId(n.chainId) === normalizedWalletChain);
    if (match && selectedNetwork?.chainId !== match.chainId) {
      setSelectedNetwork(match);
      selectedNetworkRef.current = match;
      lastNetworkChangeRef.current = { source: 'auto', at: Date.now() };
      if (typeof window !== 'undefined' && match?.chainId) {
        window.localStorage.setItem('wallet_selectedNetworkChainId', match.chainId);
      }
    }
  }, [walletChain, effectiveFilteredNetworks, selectedNetwork]);

  // Com Phantom EVM, garantir que a rede selecionada seja uma das redes permitidas (lista já filtrada por role)
  useEffect(() => {
    if (connectedWallet !== 'phantom' || !isPhantomEVM || !selectedNetwork?.chainId) return;
    const allowedChainIds = filteredPhantomEvmNetworks.map((n) => n.chainId);
    if (!allowedChainIds.includes(selectedNetwork.chainId)) {
      const fallback = filteredPhantomEvmNetworks[0];
      if (fallback) {
        setSelectedNetwork(fallback);
        selectedNetworkRef.current = fallback;
        if (typeof window !== 'undefined' && fallback.chainId) {
          window.localStorage.setItem('wallet_selectedNetworkChainId', fallback.chainId);
        }
      }
    }
  }, [connectedWallet, isPhantomEVM, filteredPhantomEvmNetworks, selectedNetwork?.chainId]);

  const getMetamaskProvider = useCallback(async () => {
    try {
      const ethereumProvider = await detectEthereumProvider();
      if (!ethereumProvider) return null;
      if (ethereumProvider.isMetaMask === true) return ethereumProvider;
      if (ethereumProvider.providers && Array.isArray(ethereumProvider.providers)) {
        const metamaskProvider = ethereumProvider.providers.find(p => p.isMetaMask === true);
        if (metamaskProvider) return metamaskProvider;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const attachListeners = useCallback((evmProvider, walletType = 'metamask') => {
    if (!evmProvider) return;
    const handleAccountsChanged = (accounts) => {
      if (!accounts || accounts.length === 0) {
        setConnectedWallet(null);
        setWalletAddress(null);
        setWalletBalance('0');
        setWalletBalanceChainId(null);
        setWalletStablecoinBalances({});
        setWalletStablecoinTotal('0');
        setWalletStablecoinChainId(null);
        return;
      }
      const address = accounts[0];
      setWalletAddress(address);
      setConnectedWallet(walletType);
      (async () => {
        try {
          const networkChainId = selectedNetworkRef.current?.chainId || SUPPORTED_NETWORKS[0].chainId;
          const { balances, total } = await fetchStablecoinBalances(networkChainId, address, evmProvider);
          setWalletStablecoinBalances(balances);
          setWalletStablecoinTotal(total);
          setWalletStablecoinChainId(networkChainId);
        } catch {
          setWalletStablecoinBalances({});
          setWalletStablecoinTotal('0');
        }
      })();
    };
    const handleChainChanged = (chainId) => setWalletChain(chainId);
    try {
      if (evmProvider.on) {
        evmProvider.on('accountsChanged', handleAccountsChanged);
        evmProvider.on('chainChanged', handleChainChanged);
      } else if (evmProvider.addListener) {
        evmProvider.addListener('accountsChanged', handleAccountsChanged);
        evmProvider.addListener('chainChanged', handleChainChanged);
      }
    } catch {
      // Listeners não disponíveis
    }
  }, []);

  const startPolling = useCallback((evmProvider, walletType = 'metamask') => {
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    let lastAddress = null;
    let tick = 0;
    const BALANCE_REFRESH_INTERVAL_TICKS = 15; // ~15s (tick a cada 1s)
    pollingIntervalRef.current = setInterval(async () => {
      tick += 1;
      try {
        const accounts = await evmProvider.request({ method: 'eth_accounts' });
        if (accounts && accounts.length > 0) {
          const currentAddress = accounts[0];
          const addressChanged = lastAddress && currentAddress !== lastAddress;
          const periodicRefresh = lastAddress === currentAddress && tick % BALANCE_REFRESH_INTERVAL_TICKS === 0;
          if (addressChanged) {
            setWalletAddress(currentAddress);
            setConnectedWallet(walletType);
          }
          if (addressChanged || periodicRefresh) {
            try {
              const networkChainId = selectedNetworkRef.current?.chainId || SUPPORTED_NETWORKS[0].chainId;
              const { balances, total } = await fetchStablecoinBalances(networkChainId, currentAddress, evmProvider);
              setWalletStablecoinBalances(balances);
              setWalletStablecoinTotal(total);
              setWalletStablecoinChainId(networkChainId);
            } catch {
              if (addressChanged) {
                setWalletStablecoinBalances({});
                setWalletStablecoinTotal('0');
              }
            }
          }
          lastAddress = currentAddress;
        } else if (lastAddress) {
          setConnectedWallet(null);
          setWalletAddress(null);
          setWalletBalance('0');
          setWalletBalanceChainId(null);
          setWalletStablecoinBalances({});
          setWalletStablecoinTotal('0');
          setWalletStablecoinChainId(null);
          lastAddress = null;
        }
      } catch {
        // Polling falhou
      }
    }, 1000);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // No mobile, chamadas ao provider (eth_accounts, etc.) disparam o prompt "Ir para MetaMask?".
  // Pausar o polling quando a aba fica oculta (usuário foi para o app) evita empilhar esses prompts.
  const pollingPausedByVisibilityRef = useRef(false);
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          pollingPausedByVisibilityRef.current = true;
        }
      } else if (document.visibilityState === 'visible' && pollingPausedByVisibilityRef.current) {
        pollingPausedByVisibilityRef.current = false;
        const prov = providerRef.current;
        const wallet = connectedWallet;
        if (prov && wallet) {
          startPolling(prov, wallet === 'phantom' ? 'phantom' : wallet === 'walletconnect' ? 'walletconnect' : 'metamask');
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [connectedWallet, startPolling]);

  const connectWalletConnect = useCallback(async () => {
    try {
      setIsConnecting(true);
      setError(null);
      if (!WALLETCONNECT_PROJECT_ID) {
        throw new Error('WalletConnect não está configurado. Defina VITE_WALLETCONNECT_PROJECT_ID no .env.');
      }
      const chainsDecimal = SUPPORTED_NETWORKS.map((n) => parseInt(n.chainId, 16));
      const wcProvider = await EthereumProvider.init({
        projectId: WALLETCONNECT_PROJECT_ID,
        optionalChains: chainsDecimal,
        showQrModal: true,
        metadata: {
          name: 'Nexusprime',
          description: 'Transferências de stablecoins com recebimento via QR Code',
          url: typeof window !== 'undefined' ? window.location.origin : 'https://pcpcryptotransfer.netlify.app',
          icons: [],
        },
      });
      walletConnectProviderRef.current = wcProvider;
      await wcProvider.connect();
      const accounts = await wcProvider.request({ method: 'eth_accounts', params: [] });
      if (!accounts || accounts.length === 0) {
        throw new Error('Nenhuma conta WalletConnect conectada');
      }
      const address = accounts[0];
      const chainIdRaw = await wcProvider.request({ method: 'eth_chainId', params: [] });
      const chainId = normalizeChainId(chainIdRaw);
      let ethBalance = '0';
      let balanceChainId = chainId;
      try {
        const result = await fetchEthBalanceWithFallbackChains(wcProvider, address);
        ethBalance = result.balance;
        balanceChainId = result.balanceChainId || chainId;
      } catch {
        // mantém 0
      }
      setWalletAddress(address);
      setConnectedWallet('walletconnect');
      if (typeof window !== 'undefined') window.localStorage.setItem(LAST_WALLET_STORAGE_KEY, 'walletconnect');
      setWalletChain(chainId);
      setWalletBalance(ethBalance);
      setWalletBalanceChainId(balanceChainId);
      setProvider(wcProvider);
      providerRef.current = wcProvider;
      // Anexa listeners para manter walletChain atualizada (chainChanged) e reagir a accountsChanged
      attachListeners(wcProvider, 'walletconnect');
      try {
        const networkChainId = selectedNetworkRef.current?.chainId || SUPPORTED_NETWORKS[0].chainId;
        const { balances, total } = await fetchStablecoinBalances(networkChainId, address, wcProvider);
        setWalletStablecoinBalances(balances);
        setWalletStablecoinTotal(total);
        setWalletStablecoinChainId(networkChainId);
      } catch {
        setWalletStablecoinBalances({});
        setWalletStablecoinTotal('0');
      }
      startPolling(wcProvider);
      success(i18n.t('connection.walletConnectSuccess'));
      return { wallet: 'walletconnect', address, chainId };
    } catch (err) {
      const msg = err?.message || '';
      const isOriginNotAllowed =
        msg.includes('origin not allowed') ||
        msg.includes('Unauthorized: origin') ||
        (typeof err?.code === 'number' && err.code === 3000);
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const errorMessage = isOriginNotAllowed
        ? i18n.t('connection.walletConnectOriginError', { origin })
        : msg || i18n.t('connection.walletConnectError');
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsConnecting(false);
    }
  }, [startPolling, success, showError]);

  const isPhantomAvailable = useCallback(() => {
    return !!(getPhantomSolanaFromWindow() || getPhantomEthereumFromWindow());
  }, []);

  const connectMetamask = useCallback(async () => {
    const now = Date.now();
    mmLoopInvokeCountRef.current += 1;
    mmLog(`connectMetamask INVOCADO #${mmLoopInvokeCountRef.current}`);

    if (lastRequestAccountsAtRef.current && now - lastRequestAccountsAtRef.current < WALLET_CONNECT_COOLDOWN_MS) {
      const waitSec = Math.ceil((WALLET_CONNECT_COOLDOWN_MS - (now - lastRequestAccountsAtRef.current)) / 1000);
      mmLog(`connectMetamask IGNORADO (cooldown): aguardar ${waitSec}s`);
      showError(i18n.t('connection.waitBeforeConnect', { seconds: waitSec }));
      return;
    }

    try {
      setIsConnecting(true);
      setError(null);
      const metamaskProvider = await getMetamaskProvider();
      if (!metamaskProvider) {
        mmLog('getMetamaskProvider retornou null');
        if (isMobile()) {
          // Em produção (host público), evitamos abrir MetaMask via deep link automaticamente
          // para não gerar múltiplos prompts "Ir para MetaMask?". O usuário deve:
          // - abrir o site dentro do navegador da própria MetaMask, ou
          // - usar WalletConnect.
          const host = typeof window !== 'undefined' ? window.location.hostname : '';
          const isLocalHost =
            host === 'localhost' ||
            host === '127.0.0.1' ||
            /^192\.168\./.test(host) ||
            /^10\./.test(host) ||
            host === 'pcpcryptotransfer.test';
          if (isLocalHost) {
            mmLog('openInMetaMaskApp() CHAMADO (apenas ambiente local/dev) — dispara prompt "Ir para MetaMask?"');
            openInMetaMaskApp();
            return;
          }
          mmLog('Metamask não detectado em host público (mobile) — instruir uso de WalletConnect ou navegador MetaMask');
          showError(i18n.t('connection.metamaskNotDetected'));
          return;
        }
        throw new Error(i18n.t('connection.metamaskNotDetected'));
      }
      attachListeners(metamaskProvider);
      let accounts = [];
      try {
        lastRequestAccountsAtRef.current = Date.now();
        mmLog('eth_requestAccounts REQUEST — dispara prompt "Ir para MetaMask?"');
        accounts = await metamaskProvider.request({ method: 'eth_requestAccounts' });
        mmLog(`eth_requestAccounts OK (${accounts?.length ?? 0} contas)`);
      } catch (e) {
        mmLogErr('eth_requestAccounts FALHOU', e);
        try {
          accounts = await metamaskProvider.request({ method: 'eth_accounts' });
        } catch (inner) {
          throw new Error(i18n.t('connection.noAccounts'));
        }
      }
      if (!accounts || accounts.length === 0) {
        throw new Error(i18n.t('connection.noAccountSelected'));
      }
      const address = accounts[0];
      const chainId = await metamaskProvider.request({ method: 'eth_chainId' });
      let ethBalance = '0';
      let balanceChainId = normalizeChainId(chainId);
      try {
        const result = await fetchEthBalanceWithFallbackChains(metamaskProvider, address);
        ethBalance = result.balance;
        balanceChainId = result.balanceChainId || balanceChainId;
      } catch {
        // Saldo permanece 0
      }
      setWalletAddress(address);
      setConnectedWallet('metamask');
      if (typeof window !== 'undefined') window.localStorage.setItem(LAST_WALLET_STORAGE_KEY, 'metamask');
      setWalletChain(chainId);
      setWalletBalance(ethBalance);
      setWalletBalanceChainId(balanceChainId);
      setProvider(metamaskProvider);
      providerRef.current = metamaskProvider;
      try {
        const networkChainId = selectedNetworkRef.current?.chainId || SUPPORTED_NETWORKS[0].chainId;
        const { balances, total } = await fetchStablecoinBalances(networkChainId, address, metamaskProvider);
        setWalletStablecoinBalances(balances);
        setWalletStablecoinTotal(total);
        setWalletStablecoinChainId(networkChainId);
      } catch {
        setWalletStablecoinBalances({});
        setWalletStablecoinTotal('0');
      }
      startPolling(metamaskProvider);
      const host = typeof window !== 'undefined' ? window.location.hostname : '';
      const isLocalOrIP = host === 'pcpcryptotransfer.test' || /^(\d+\.\d+\.\d+\.\d+|localhost|127\.0\.0\.1)$/.test(host);
      success(isLocalOrIP
        ? i18n.t('connection.metamaskSuccessLocal')
        : i18n.t('connection.metamaskSuccess'));
      return { wallet: 'metamask', address, chainId };
    } catch (err) {
      const errorMessage = err.message || i18n.t('connection.metamaskError');
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsConnecting(false);
    }
  }, [getMetamaskProvider, attachListeners, success, showError, mmLog, mmLogErr]);

  /** Conecta EVM: tenta MetaMask (extensão) primeiro; se não houver, abre WalletConnect (QR). */
  const connectEVM = useCallback(async () => {
    const metamaskProvider = await getMetamaskProvider();
    if (metamaskProvider) {
      return connectMetamask();
    }
    return connectWalletConnect();
  }, [getMetamaskProvider, connectMetamask, connectWalletConnect]);

  const connectPhantom = useCallback(async () => {
    try {
      setIsConnecting(true);
      setError(null);

      const phantomEthereum = getPhantomEthereumFromWindow();
      if (phantomEthereum) {
        attachListeners(phantomEthereum, 'phantom');
        let accounts = [];
        try {
          accounts = await phantomEthereum.request({ method: 'eth_requestAccounts' });
        } catch (e) {
          try {
            accounts = await phantomEthereum.request({ method: 'eth_accounts' });
          } catch {
            throw new Error(i18n.t('connection.noAccounts'));
          }
        }
        if (!accounts || accounts.length === 0) {
          throw new Error(i18n.t('connection.noAccountSelected'));
        }
        const address = accounts[0];
        const chainId = await phantomEthereum.request({ method: 'eth_chainId' });
        let ethBalance = '0';
        let balanceChainId = normalizeChainId(chainId);
        try {
          const result = await fetchEthBalanceWithFallbackChains(phantomEthereum, address);
          ethBalance = result.balance;
          balanceChainId = result.balanceChainId || balanceChainId;
        } catch {
          // saldo permanece 0
        }
        setWalletAddress(address);
        setConnectedWallet('phantom');
        setIsPhantomEVM(true);
        if (typeof window !== 'undefined') window.localStorage.setItem(LAST_WALLET_STORAGE_KEY, 'phantom');
        setWalletChain(chainId);
        setWalletBalance(ethBalance);
        setWalletBalanceChainId(balanceChainId);
        setProvider(phantomEthereum);
        providerRef.current = phantomEthereum;
        try {
          const networkChainId = selectedNetworkRef.current?.chainId || SUPPORTED_NETWORKS[0].chainId;
          const { balances, total } = await fetchStablecoinBalances(networkChainId, address, phantomEthereum);
          setWalletStablecoinBalances(balances);
          setWalletStablecoinTotal(total);
          setWalletStablecoinChainId(networkChainId);
        } catch {
          setWalletStablecoinBalances({});
          setWalletStablecoinTotal('0');
        }
        startPolling(phantomEthereum, 'phantom');
        success(i18n.t('connection.phantomSuccess'));
        return { wallet: 'phantom', address, chainId };
      }

      const provider = getPhantomSolanaFromWindow();
      void ({
        hasWindowSolana: typeof window !== 'undefined' && !!window.solana,
        hasPhantomSolana: !!provider,
        isPhantom: !!provider?.isPhantom,
      });
      if (!provider) {
        throw new Error(i18n.t('connection.phantomNotInstalled'));
      }
      const CONNECT_TIMEOUT_MS = 25000;
      const useRequest = typeof provider.request === 'function';
      const connectPromise = useRequest
        ? provider.request({ method: 'connect' })
        : provider.connect();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Phantom connection timeout — approve in the Phantom extension or try again.')), CONNECT_TIMEOUT_MS)
      );
      const response = await Promise.race([connectPromise, timeoutPromise]);
      if (!response?.publicKey) {
        throw new Error('Phantom did not return a public key');
      }
      const publicKey = response.publicKey.toString();
      setWalletAddress(publicKey);
      setConnectedWallet('phantom');
      setIsPhantomEVM(false);
      if (typeof window !== 'undefined') window.localStorage.setItem(LAST_WALLET_STORAGE_KEY, 'phantom');
      setWalletChain('solana');
      setWalletBalance('0');
      setProvider(provider);
      success(i18n.t('connection.phantomSuccess'));
      return { wallet: 'phantom', address: publicKey, chain: 'solana' };
    } catch (err) {
      const errorMessage = err.message || i18n.t('connection.phantomError');
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsConnecting(false);
    }
  }, [attachListeners, startPolling, success, showError]);

  const disconnectWallet = useCallback(async () => {
    try {
      stopPolling();
      if (connectedWallet === 'phantom') {
        const solana = getPhantomSolanaFromWindow();
        if (solana) {
          try { await solana.disconnect(); } catch { /* ignora */ }
        }
      } else if (connectedWallet === 'walletconnect' && walletConnectProviderRef.current) {
        try {
          await walletConnectProviderRef.current.disconnect();
        } catch {
          // ignora erro de disconnect
        }
      }
      setConnectedWallet(null);
      setWalletAddress(null);
      setWalletChain(null);
      setWalletBalance('0');
      setWalletBalanceChainId(null);
      setWalletStablecoinBalances({});
      setWalletStablecoinTotal('0');
      setWalletStablecoinChainId(null);
      setIsPhantomEVM(false);
      setProvider(null);
      setError(null);
      providerRef.current = null;
      walletConnectProviderRef.current = null;
      success(i18n.t('connection.disconnectSuccess'));
    } catch (err) {
      showError(i18n.t('connection.disconnectError'));
    }
  }, [connectedWallet, stopPolling, success, showError]);

  useEffect(() => {
    const checkConnected = async () => {
      try {
        const lastWallet = typeof window !== 'undefined' ? window.localStorage.getItem(LAST_WALLET_STORAGE_KEY) : null;
        const validWallets = ['metamask', 'phantom', 'walletconnect'];
        const order =
          lastWallet && validWallets.includes(lastWallet)
            ? [lastWallet, ...validWallets.filter((w) => w !== lastWallet)]
            : validWallets;

        for (const walletType of order) {
          if (walletType === 'metamask') {
            const metamaskProvider = await getMetamaskProvider();
            if (metamaskProvider) {
              setProvider(metamaskProvider);
              providerRef.current = metamaskProvider;
              attachListeners(metamaskProvider);
              startPolling(metamaskProvider);
              try {
                const accounts = await metamaskProvider.request({ method: 'eth_accounts' });
                const chainId = await metamaskProvider.request({ method: 'eth_chainId' }).catch(() => null);
                if (chainId) setWalletChain(chainId);
                if (accounts && accounts.length > 0) {
                  const address = accounts[0];
                  setWalletAddress(address);
                  setConnectedWallet('metamask');
                  if (typeof window !== 'undefined') window.localStorage.setItem(LAST_WALLET_STORAGE_KEY, 'metamask');
                  try {
                    const result = await fetchEthBalanceWithFallbackChains(metamaskProvider, address);
                    setWalletBalance(result.balance);
                    setWalletBalanceChainId(result.balanceChainId || normalizeChainId(chainId));
                  } catch {
                    setWalletBalance('0');
                  }
                  try {
                    const networkChainId = selectedNetworkRef.current?.chainId || SUPPORTED_NETWORKS[0].chainId;
                    const { balances, total } = await fetchStablecoinBalances(networkChainId, address, metamaskProvider);
                    setWalletStablecoinBalances(balances);
                    setWalletStablecoinTotal(total);
                    setWalletStablecoinChainId(networkChainId);
                  } catch {
                    setWalletStablecoinBalances({});
                    setWalletStablecoinTotal('0');
                  }
                  return;
                }
              } catch {
                // Ignorar
              }
            }
          }
          if (walletType === 'phantom') {
            const phantomEth = getPhantomEthereumFromWindow();
            if (phantomEth) {
              try {
                const accounts = await phantomEth.request({ method: 'eth_accounts' });
                if (accounts && accounts.length > 0) {
                  providerRef.current = phantomEth;
                  setProvider(phantomEth);
                  attachListeners(phantomEth, 'phantom');
                  startPolling(phantomEth, 'phantom');
                  const address = accounts[0];
                  const chainId = await phantomEth.request({ method: 'eth_chainId' }).catch(() => null);
                  setWalletAddress(address);
                  setConnectedWallet('phantom');
                  setIsPhantomEVM(true);
                  if (typeof window !== 'undefined') window.localStorage.setItem(LAST_WALLET_STORAGE_KEY, 'phantom');
                  if (chainId) setWalletChain(chainId);
                  try {
                    const result = await fetchEthBalanceWithFallbackChains(phantomEth, address);
                    setWalletBalance(result.balance);
                    setWalletBalanceChainId(result.balanceChainId || normalizeChainId(chainId));
                  } catch {
                    setWalletBalance('0');
                  }
                  try {
                    const networkChainId = selectedNetworkRef.current?.chainId || SUPPORTED_NETWORKS[0].chainId;
                    const { balances, total } = await fetchStablecoinBalances(networkChainId, address, phantomEth);
                    setWalletStablecoinBalances(balances);
                    setWalletStablecoinTotal(total);
                    setWalletStablecoinChainId(networkChainId);
                  } catch {
                    setWalletStablecoinBalances({});
                    setWalletStablecoinTotal('0');
                  }
                  return;
                }
              } catch {
                // Phantom EVM sem sessão
              }
            }
          }
          if (walletType === 'walletconnect' && WALLETCONNECT_PROJECT_ID) {
            try {
              const chainsDecimal = SUPPORTED_NETWORKS.map((n) => parseInt(n.chainId, 16));
              const wcProvider = await EthereumProvider.init({
                projectId: WALLETCONNECT_PROJECT_ID,
                optionalChains: chainsDecimal,
                showQrModal: false,
                metadata: {
                  name: 'Nexusprime',
                  description: 'Transferências de stablecoins com recebimento via QR Code',
                  url: typeof window !== 'undefined' ? window.location.origin : '',
                  icons: [],
                },
              });
              const accounts = await wcProvider.request({ method: 'eth_accounts', params: [] });
              if (accounts && accounts.length > 0) {
                walletConnectProviderRef.current = wcProvider;
                providerRef.current = wcProvider;
                setProvider(wcProvider);
                const address = accounts[0];
                const chainIdRaw = await wcProvider.request({ method: 'eth_chainId', params: [] }).catch(() => null);
                const chainId = normalizeChainId(chainIdRaw);
                setWalletAddress(address);
                setConnectedWallet('walletconnect');
                if (typeof window !== 'undefined') window.localStorage.setItem(LAST_WALLET_STORAGE_KEY, 'walletconnect');
                setWalletChain(chainId);
                setWalletBalance('0');
                setWalletBalanceChainId(chainId);
                setWalletStablecoinBalances({});
                setWalletStablecoinTotal('0');
                try {
                  const result = await fetchEthBalanceWithFallbackChains(wcProvider, address);
                  setWalletBalance(result.balance);
                  setWalletBalanceChainId(result.balanceChainId || chainId);
                } catch {
                  // mantém 0
                }
                try {
                  const networkChainId = selectedNetworkRef.current?.chainId || SUPPORTED_NETWORKS[0].chainId;
                  const { balances, total } = await fetchStablecoinBalances(networkChainId, address, wcProvider);
                  setWalletStablecoinBalances(balances);
                  setWalletStablecoinTotal(total);
                  setWalletStablecoinChainId(networkChainId);
                } catch {
                  // ignora
                }
                // Garante listeners para chainChanged/accountsChanged em sessões restauradas
                attachListeners(wcProvider, 'walletconnect');
                startPolling(wcProvider);
                return;
              }
            } catch {
              // Sem sessão WalletConnect ou erro ao restaurar
            }
          }
        }

        const phantomSolana = getPhantomSolanaFromWindow();
        if (phantomSolana?.isConnected) {
          try {
            setWalletAddress(phantomSolana.publicKey.toString());
            setConnectedWallet('phantom');
            setIsPhantomEVM(false);
            if (typeof window !== 'undefined') window.localStorage.setItem(LAST_WALLET_STORAGE_KEY, 'phantom');
            setProvider(phantomSolana);
            providerRef.current = phantomSolana;
          } catch {
            // Ignorar
          }
        }
      } finally {
        setIsWalletInitializing(false);
      }
    };
    checkConnected();
    return () => stopPolling();
  }, [getMetamaskProvider, attachListeners, startPolling, stopPolling]);

  const switchNetwork = useCallback(
    async (network) => {
      if (!network || network.chainId === selectedNetwork?.chainId) return;
      setSelectedNetwork(network);
      lastNetworkChangeRef.current = { source: 'user', at: Date.now() };
      if (typeof window !== 'undefined' && network?.chainId) {
        window.localStorage.setItem('wallet_selectedNetworkChainId', network.chainId);
      }
      const prov = providerRef.current;
      const wallet = connectedWallet;
      const isEVM = wallet === 'metamask' || wallet === 'walletconnect' || wallet === 'phantom';
      // Troca a rede na carteira neste gesto (seleção no header), para que no clique "Enviar"
      // só dispare o pedido de assinatura da transação — evita dois prompts no mesmo clique
      // e que o segundo (transação) não chegue à MetaMask (empilhamento de "abrir MetaMask").
      if (walletAddress && isEVM && prov?.request && network?.chainId) {
        try {
          await prov.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: network.chainId }],
          });
        } catch (err) {
          const code = err?.code;
          if (code !== 4902 && code !== '4902') {
            // opcional: poderia mostrar toast; por ora só ignora e deixa o envio tratar mismatch
          }
        }
      }
      if (walletAddress && isEVM && prov) {
        try {
          const { balances, total } = await fetchStablecoinBalances(network.chainId, walletAddress, prov);
          setWalletStablecoinBalances(balances);
          setWalletStablecoinTotal(total);
          setWalletStablecoinChainId(network.chainId);
        } catch {
          setWalletStablecoinBalances({});
          setWalletStablecoinTotal('0');
        }
      }
    },
    [selectedNetwork, walletAddress, connectedWallet]
  );

  const refreshWalletBalances = useCallback(async () => {
    const addr = walletAddress;
    const wallet = connectedWallet;
    if (!addr || !providerRef.current) return;
    const isEVM =
      wallet === 'metamask' ||
      wallet === 'walletconnect' ||
      (wallet === 'phantom' && isPhantomEVM);
    if (!isEVM) return;
    try {
      const networkChainId = selectedNetworkRef.current?.chainId || SUPPORTED_NETWORKS[0].chainId;
      const { balances, total } = await fetchStablecoinBalances(networkChainId, addr, providerRef.current);
      setWalletStablecoinBalances(balances);
      setWalletStablecoinTotal(total);
      setWalletStablecoinChainId(networkChainId);
    } catch {
      // ignorar falha ao atualizar saldo
    }
  }, [walletAddress, connectedWallet, isPhantomEVM]);

  const value = {
    connectedWallet,
    walletAddress,
    walletChain,
    walletBalance,
    walletBalanceChainId,
    walletStablecoinBalances,
    walletStablecoinTotal,
    walletStablecoinChainId,
    selectedNetwork,
    supportedNetworks: effectiveFilteredNetworks,
    getChainDisplayName,
    isConnecting,
    isWalletInitializing,
    isPhantomEVM,
    error,
    provider,
    isPhantomAvailable,
    connectMetamask,
    connectPhantom,
    connectWalletConnect,
    connectEVM,
    disconnectWallet,
    switchNetwork,
    refreshWalletBalances,
    isMobile: isMobile(),
    openInMetaMaskApp,
  };

  return (
    <WalletConnectionContext.Provider value={value}>
      {children}
    </WalletConnectionContext.Provider>
  );
}
